import { resolveNarrativeEngineMode } from '../narrativeEngineMode';
import { createGoldenGenerateBinding } from './adapters/goldenGenerateBinding';
import type { OriginalEngineAITransport } from './types';
import {
  runOriginalOrchestrationToCompletion,
  runOriginalOrchestrationFromResumeStage,
  createInitialLoopState,
  type OriginalOrchestrationStageHookPayload,
} from './golden/goldenOrchestrationLoop';
import type {
  OriginalEngineCompatRunInput,
  OriginalEngineCompatRunResult,
  OriginalEngineEntitlementGateway,
  OriginalEngineCompatRepository,
} from './types';
import {
  buildCompatCheckpointBody,
  compatCheckpointStoragePath,
  validateCheckpointIntegrity,
} from './compat/compatCheckpoint.service';
import { buildCommitKey } from './compat/compatTypes';
import { routeCompatReservationError } from './compat/compatCreditRouting.service';

const STAGING_FAILURE_ENABLED =
  import.meta.env.VITE_STAGING_MODE === 'true' &&
  import.meta.env.VITE_ORIGINAL_ENGINE_STAGING_FAILURE_INJECT === 'true';

export interface OriginalEngineCompatRunnerDeps {
  transport: OriginalEngineAITransport;
  repository: OriginalEngineCompatRepository;
  entitlement: OriginalEngineEntitlementGateway;
  engineMode?: string;
  uid?: string;
}

export class OriginalEngineCompatRunner {
  constructor(private readonly deps: OriginalEngineCompatRunnerDeps) {}

  async run(input: OriginalEngineCompatRunInput): Promise<OriginalEngineCompatRunResult> {
    const mode = this.deps.engineMode ?? 'ORIGINAL_ENGINE_COMPAT';
    try {
      resolveNarrativeEngineMode(mode);
    } catch (error: any) {
      return fail(input, 'INVALID_ENGINE_MODE', error.message, false);
    }

    let reservationId: string | null = null;

    try {
      const project = await this.deps.repository.loadProject(input.projectId);
      const chapterNumber = input.chapterNumber ?? project.currentChapterNumber;

      const pendingCommit = await this.deps.repository.getCommitRecord(buildCommitKey({
        projectId: input.projectId,
        chapterNumber,
        runId: input.runId,
      }));
      if (pendingCommit?.status === 'complete') {
        return {
          ok: true,
          projectId: input.projectId,
          runId: input.runId,
          chapterNumber,
          chapterTitle: `Chapter ${chapterNumber}`,
          nextChapterNumber: pendingCommit.nextChapterNumber || chapterNumber + 1,
          inspectorStatus: 'PASS',
          correctionCount: 0,
          editorStatus: 'APPROVED',
          reservationConsumed: true,
        };
      }

      const recoverable = await this.deps.repository.loadRecoverableCheckpoint(input.projectId, chapterNumber);

      if (pendingCommit?.status === 'storage_complete') {
        const body = recoverable?.body;
        const commitOutcome = await this.deps.repository.retryCommitPending({
          projectId: input.projectId,
          chapterNumber,
          runId: input.runId,
          chapter: body?.chapterDraft || {
            id: chapterNumber,
            title: `Chapter ${chapterNumber}`,
            content: '',
            summary: '',
            status: 'complete',
          },
          ledger: body?.updatedLedger || project.ledger,
          storyBible: project.storyBible,
          chapters: [
            ...project.chapters.filter((c) => c.id !== chapterNumber),
            body?.chapterDraft || {
              id: chapterNumber,
              title: `Chapter ${chapterNumber}`,
              content: '',
              summary: '',
              status: 'complete',
            },
          ].sort((a, b) => a.id - b.id),
          nextChapterNumber: chapterNumber + 1,
        });
        if (commitOutcome.committed) {
          let reservationId: string | null = null;
          try {
            reservationId = (await this.deps.entitlement.reserve({
              projectId: input.projectId,
              chapterNumber,
              runId: input.runId,
            })).reservationId;
            if (!commitOutcome.idempotentReplay) {
              await this.deps.entitlement.consume(reservationId);
            }
          } catch {
            // commit already durable; entitlement may already be consumed
          }
          await this.deps.repository.deleteSuccessfulCheckpoint(input.projectId, chapterNumber);
          return {
            ok: true,
            projectId: input.projectId,
            runId: input.runId,
            chapterNumber,
            chapterTitle: recoverable.body.chapterDraft.title || `Chapter ${chapterNumber}`,
            nextChapterNumber: chapterNumber + 1,
            inspectorStatus: 'PASS',
            correctionCount: recoverable.body.originalRetryCount,
            editorStatus: 'APPROVED',
            reservationConsumed: true,
          };
        }
      }

      if (input.resumeFromCheckpoint && recoverable?.body) {
        const integrity = await validateCheckpointIntegrity({
          checkpoint: recoverable.body,
          storyBible: project.storyBible,
          ledger: project.ledger,
          chapterNumber,
        });
        if (!integrity.ok) {
          return {
            ok: false,
            projectId: input.projectId,
            runId: input.runId,
            code: 'CHECKPOINT_INTEGRITY_BLOCKED',
            message: 'Checkpoint no longer matches project canon. Review recovery options.',
            reservationReleased: false,
            checkpointRetained: true,
            integrityBlocked: true,
          };
        }
      }

      reservationId = (await this.deps.entitlement.reserve({
        projectId: input.projectId,
        chapterNumber,
        runId: input.runId,
      })).reservationId;

      createGoldenGenerateBinding(this.deps.transport, {
        projectId: input.projectId,
        chapterNumber,
      });

      const uid = this.deps.uid || 'local';
      const loopBase = createInitialLoopState({
        storyBible: project.storyBible,
        ledger: project.ledger,
        chapterNumber,
        previousChapters: project.chapters,
        correctionOrder: recoverable?.body?.correctionOrder,
        failedDraft: recoverable?.body?.chapterDraft?.content,
        retryCount: recoverable?.body?.originalRetryCount,
        directorsNote: recoverable?.body?.loopState?.directorsNote,
      });
      if (recoverable?.body?.loopState) {
        Object.assign(loopBase, recoverable.body.loopState);
      }

      const stageHooks = {
        onStageComplete: async (payload: OriginalOrchestrationStageHookPayload) => {
          if (
            STAGING_FAILURE_ENABLED &&
            input.forceStagingFailureAfter &&
            payload.stage === input.forceStagingFailureAfter
          ) {
            throw new Error(`STAGING_FAILURE_INJECT:${payload.stage}`);
          }
          const body = await buildCompatCheckpointBody({
            projectId: input.projectId,
            chapterNumber,
            runId: input.runId,
            resumeStage: payload.stage,
            storyBible: project.storyBible,
            ledger: project.ledger,
            chapterDraft: payload.chapterDraft!,
            originalRetryCount: payload.state.retryCount,
            inspectorResult: payload.inspectorResult,
            correctionOrder: payload.state.correctionOrder,
            loopState: payload.state,
            editorResult: payload.editorResult,
            publicationResult: payload.publicationResult,
            updatedLedger: payload.updatedLedger,
          });
          const storagePath = compatCheckpointStoragePath(uid, input.projectId, chapterNumber, input.runId);
          try {
            await this.deps.repository.saveStageCheckpoint(body, storagePath);
          } catch {
            // Checkpoint persistence is infrastructure-only; do not abort golden narrative stages.
          }
        },
      };

      const orchestration =
        input.resumeFromCheckpoint && recoverable?.body && recoverable.body.resumeStage !== 'COMMIT_PENDING'
          ? await runOriginalOrchestrationFromResumeStage(
              loopBase,
              recoverable.body.resumeStage,
              {
                chapterDraft: recoverable.body.chapterDraft,
                inspectorResult: recoverable.body.inspectorResult,
                editorResult: recoverable.body.editorResult,
                publicationResult: recoverable.body.publicationResult,
                updatedLedger: recoverable.body.updatedLedger,
              },
              stageHooks,
            )
          : await runOriginalOrchestrationToCompletion(loopBase, 12, stageHooks);

      const nextChapters = [
        ...project.chapters.filter((c) => c.id !== chapterNumber),
        orchestration.chapter,
      ].sort((a, b) => a.id - b.id);

      const commitOutcome = await (async () => {
        try {
          return await this.deps.repository.commitChapter({
            projectId: input.projectId,
            chapterNumber,
            runId: input.runId,
            chapter: orchestration.chapter,
            ledger: orchestration.ledger,
            storyBible: project.storyBible,
            chapters: nextChapters,
            nextChapterNumber: orchestration.nextChapterNumber,
          });
        } catch (commitError: any) {
          if (String(commitError?.message || '').includes('metadata commit failure')) {
            const pendingBody = await buildCompatCheckpointBody({
              projectId: input.projectId,
              chapterNumber,
              runId: input.runId,
              resumeStage: 'COMMIT_PENDING',
              storyBible: project.storyBible,
              ledger: project.ledger,
              chapterDraft: orchestration.chapter,
              originalRetryCount: orchestration.correctionCount,
              inspectorResult: orchestration.inspectorResult,
              editorResult: orchestration.editorResult,
              publicationResult: orchestration.publicationResult,
              updatedLedger: orchestration.ledger,
              loopState: orchestration.loopState,
            });
            const storagePath = compatCheckpointStoragePath(uid, input.projectId, chapterNumber, input.runId);
            await this.deps.repository.saveStageCheckpoint(pendingBody, storagePath);
          }
          throw commitError;
        }
      })();

      if (!commitOutcome.idempotentReplay) {
        await this.deps.entitlement.consume(reservationId);
      } else {
        const existing = await this.deps.repository.getCommitRecord(buildCommitKey({
          projectId: input.projectId,
          chapterNumber,
          runId: input.runId,
        }));
        if (existing?.status === 'complete') {
          await this.deps.entitlement.consume(reservationId);
        }
      }

      await this.deps.repository.deleteSuccessfulCheckpoint(input.projectId, chapterNumber);

      return {
        ok: true,
        projectId: input.projectId,
        runId: input.runId,
        chapterNumber,
        chapterTitle: orchestration.chapter.title || `Chapter ${chapterNumber}`,
        nextChapterNumber: orchestration.nextChapterNumber,
        inspectorStatus: orchestration.inspectorResult.status,
        correctionCount: orchestration.correctionCount,
        editorStatus: orchestration.editorResult.status,
        reservationConsumed: true,
      };
    } catch (error: any) {
      const creditRoute = routeCompatReservationError({
        error,
        projectId: input.projectId,
        chapterNumber: input.chapterNumber || 1,
        targetChapterCount: 12,
      });

      if (creditRoute.action === 'open_paywall') {
        return {
          ok: false,
          projectId: input.projectId,
          runId: input.runId,
          code: 'NO_MANUSCRIPT_ENTITLEMENT',
          message: creditRoute.message,
          reservationReleased: false,
          openPaywall: true,
        };
      }

      if (
        reservationId &&
        !String(error?.message || '').includes('STAGING_FAILURE_INJECT') &&
        !String(error?.message || '').includes('metadata commit failure')
      ) {
        try {
          await this.deps.entitlement.release(reservationId, error?.code || error?.message || 'run_failed');
        } catch {
          // best effort
        }
      }

      if (String(error?.message || '').includes('STAGING_FAILURE_INJECT')) {
        return {
          ok: false,
          projectId: input.projectId,
          runId: input.runId,
          code: 'STAGING_FAILURE_INJECT',
          message: 'Staging failure injected after completed stage. Checkpoint retained.',
          reservationReleased: Boolean(reservationId),
          checkpointRetained: true,
        };
      }

      if (String(error?.message || '').includes('metadata commit failure')) {
        return {
          ok: false,
          projectId: input.projectId,
          runId: input.runId,
          code: 'COMMIT_PENDING',
          message: 'Chapter prose saved. Commit pending — retry to complete.',
          reservationReleased: false,
          checkpointRetained: true,
        };
      }

      return {
        ok: false,
        projectId: input.projectId,
        runId: input.runId,
        code: error?.code || error?.name || 'RUN_FAILED',
        message: error?.message || 'Original engine compatibility run failed',
        reservationReleased: Boolean(reservationId),
        checkpointRetained: creditRoute.action === 'paused_recovery' || creditRoute.action === 'technical_failure',
      };
    }
  }
}

function fail(
  input: OriginalEngineCompatRunInput,
  code: string,
  message: string,
  released: boolean,
): OriginalEngineCompatRunResult {
  return {
    ok: false,
    projectId: input.projectId,
    runId: input.runId,
    code,
    message,
    reservationReleased: released,
  };
}
